/**
 * #4340 — mensagem que chega atrasada precisa entrar na POSIÇÃO CRONOLÓGICA.
 *
 * O reducer da conversa fazia `state.push(novaMensagem)`: toda mensagem vinda
 * pelo socket ia para o fim da lista, isto é, era desenhada como a mais
 * recente. Enquanto tudo chega em ordem isso funciona.
 *
 * Quebra quando o plugin reentrega mensagens antigas — tipicamente depois de
 * reiniciar a stack, drenando o outbox. A mensagem tem `createdAt` correto (de
 * dias atrás), mas aparece embaixo de tudo, depois do bloco "Hoje". Foi o
 * relato do #4340: "as conversas do dia 12 aparecem antes das do dia 11" e
 * "o histórico do dia 11 ficou embaixo do de hoje".
 *
 * Como os separadores de data ("Hoje", "Ontem") são desenhados comparando cada
 * mensagem com a anterior da lista, uma mensagem fora de lugar também bagunça
 * as tarjas de data.
 */

/** Data comparável de uma mensagem. Sem createdAt, devolve null. */
function tempoDe(mensagem) {
  const bruto = mensagem?.createdAt;
  if (!bruto) return null;
  const t = new Date(bruto).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * Insere `nova` em `lista` (ordenada por createdAt crescente) mantendo a ordem.
 *
 * Percorre do fim para o começo porque o caso comum — de longe — é a mensagem
 * nova ser a mais recente: nesse caminho para na primeira comparação e o custo
 * é o mesmo do push que existia antes.
 *
 * Sem data utilizável, cai no fim, que é o comportamento antigo: melhor
 * mostrar a mensagem em posição duvidosa do que escondê-la.
 */
export function inserirEmOrdem(lista, nova) {
  const t = tempoDe(nova);
  if (t === null) return [...lista, nova];

  let i = lista.length;
  while (i > 0) {
    const anterior = tempoDe(lista[i - 1]);
    // Mensagem sem data não serve de referência: continua procurando.
    if (anterior !== null && anterior <= t) break;
    i -= 1;
  }

  return [...lista.slice(0, i), nova, ...lista.slice(i)];
}

/**
 * Ordena a conversa por data, preservando a ordem relativa de quem empata
 * (o `sort` do JS é estável) — assim mensagens do mesmo segundo não ficam
 * trocando de lugar a cada render.
 *
 * Mensagens sem data mantêm a posição em que já estavam.
 */
export function ordenarMensagens(lista) {
  return [...lista].sort((a, b) => {
    const ta = tempoDe(a);
    const tb = tempoDe(b);
    if (ta === null || tb === null) return 0;
    return ta - tb;
  });
}

export default inserirEmOrdem;
